'use client'

import React, { useEffect, useState, useRef } from 'react'
import { Box, Flex, Stack, Text, Title, UnstyledButton } from '@mantine/core'
import { motion } from 'framer-motion'
import { useModal } from '@/hooks/useModal'
import { useBreakpoint } from '@/hooks/useBreakpoints'
import { getCategoryPageData } from '@/lib/hardcoded-data/CategoryPages/getCategoryPageData'
import type {
    TestimonialUgcData,
    TestimonialUgcImageCard,
    TestimonialUgcQuoteCard,
    TestimonialUgcVideoCard,
} from '@/lib/hardcoded-data/CategoryPages/types'
import ModalManager from '@/components/Refresh/Modals/ModalManager/ModalManager'
import { CardSlider } from '@/components/Refresh/CardSlider/CardSlider'
import { CardSliderCard } from '@/components/Refresh/CardSlider/CardSliderCard'
import { FullBleedWrapper } from '../FullBleedWrapper'
import { slideInVariants, staggerContainer, textFadeInVariantsNoTransition } from '@/lib/animations'
import type { CategoryWidgetProps } from '../types'
import { MotionBox } from '@/components/shared/AnimatedComponents'
import { refreshTextVariantProps, refreshTitleVariantProps } from '@/components/Refresh/RefreshTypography'

const CARD_GAP = 24
const CARD_RADIUS = 16
const LONG_QUOTE_CHAR_THRESHOLD = 200

/** Quote text presets: long quotes use smaller size (18/24px), short quotes use larger (22/32px). Letter-spacing kept per design. */
const quoteTextStyles = (isLong: boolean) => ({
    base: {
        fz: isLong ? 18 : 22,
        fw: 400,
        lh: '120%',
        letterSpacing: isLong ? '-0.534px' : '-0.684px',
    },
    sm: {
        fz: isLong ? 24 : 32,
        fw: 400,
        lh: '120%',
        letterSpacing: isLong ? '-0.48px' : '-0.64px',
    },
})

function VideoCard({
    card,
    onPlayClick,
    isSm,
}: {
    card: TestimonialUgcVideoCard
    onPlayClick: () => void
    isSm: boolean
}) {
    const videoSrc = card.placeholderVideoSrc ?? card.videoUrl
    const isLongQuote = (card.quote?.length ?? 0) > LONG_QUOTE_CHAR_THRESHOLD
    const quoteStyles = quoteTextStyles(isLongQuote)
    const videoSrcStr = typeof videoSrc === 'string' ? videoSrc : (videoSrc as { src: string }).src

    return (
        <Box
            w={{ base: 294, sm: 420 }}
            h={{ base: 650, sm: 685 }}
            p={{ base: 24, sm: 48 }}
            pos="relative"
            style={{
                flexShrink: 0,
                boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
                borderRadius: CARD_RADIUS,
                overflow: 'hidden',
            }}
        >
            <Box pos="absolute" inset={0} w="100%" h="100%" style={{ zIndex: 0 }}>
                <video
                    src={videoSrcStr}
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="metadata"
                    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                />
            </Box>
            <Box
                pos="absolute"
                inset={0}
                style={{
                    background:
                        'linear-gradient(to top, rgba(0, 0, 0, 0.75) 0%, rgba(0, 0, 0, 0.4) 40%, transparent 70%)',
                    zIndex: 1,
                }}
            />
            <Flex
                direction="column"
                justify="flex-end"
                align="flex-start"
                pos="absolute"
                inset={0}
                p={{ base: 24, sm: 48 }}
                style={{ zIndex: 2 }}
            >
                <Flex direction="column" gap={24} align="flex-start">
                    <Text
                        component="p"
                        c="white"
                        fz={{ base: quoteStyles.base.fz, sm: quoteStyles.sm.fz }}
                        fw={quoteStyles.base.fw}
                        lh={quoteStyles.base.lh}
                        m={0}
                        style={{
                            letterSpacing: isSm ? quoteStyles.base.letterSpacing : quoteStyles.sm.letterSpacing,
                        }}
                    >
                        {card.quote}
                    </Text>
                    <Text c="white" fz={{ base: 16, sm: 20 }} lh="130%" m={0} fw={450}>
                        {card.place ? `${card.name}, ${card.place}` : card.name}
                    </Text>
                </Flex>
            </Flex>
            <UnstyledButton
                pos="absolute"
                top={{ base: 24, sm: 48 }}
                left={{ base: 24, sm: 48 }}
                style={{ zIndex: 3, transition: 'transform 150ms ease, opacity 150ms ease' }}
                onClick={(e) => {
                    e.stopPropagation()
                    onPlayClick()
                }}
                aria-label="Play video"
                styles={{
                    root: {
                        '&:hover': { transform: 'scale(1.08)', opacity: 0.95 },
                        '&:active': { transform: 'scale(0.98)' },
                    },
                }}
            >
                <PlayIconWhite />
            </UnstyledButton>
        </Box>
    )
}

function ImageCard({ card, isSm }: { card: TestimonialUgcImageCard; isSm: boolean }) {
    const isLongQuote = (card.quote?.length ?? 0) > LONG_QUOTE_CHAR_THRESHOLD
    const quoteStyles = quoteTextStyles(isLongQuote)

    return (
        <Box
            w={{ base: 294, sm: 400 }}
            p={{ base: 24, sm: 32 }}
            pos="relative"
            bg="rgba(20, 6, 140, 0.10)"
            style={{
                flexShrink: 0,
                boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
                overflow: 'hidden',
                borderRadius: CARD_RADIUS,
            }}
        >
            <Stack gap={24} h="100%">
                <Flex direction="column" justify="space-between" gap={24} style={{ flex: 1, minHeight: 0 }}>
                    <Text
                        component="p"
                        c="gray.9"
                        fz={{ base: quoteStyles.base.fz, sm: quoteStyles.sm.fz }}
                        fw={quoteStyles.base.fw}
                        lh={quoteStyles.base.lh}
                        m={0}
                        style={{
                            letterSpacing: isSm ? quoteStyles.base.letterSpacing : quoteStyles.sm.letterSpacing,
                        }}
                    >
                        “{card.quote}”
                    </Text>
                    <Text c="gray.7" fz={{ base: 16, sm: 20 }} lh="130%" m={0} fw={450}>
                        {card.place ? `${card.name}, ${card.place}` : card.name}
                    </Text>
                </Flex>
            </Stack>
        </Box>
    )
}

function QuoteCard({ card, isSm }: { card: TestimonialUgcQuoteCard; isSm: boolean }) {
    const isLongQuote = (card.quote?.length ?? 0) > LONG_QUOTE_CHAR_THRESHOLD
    const quoteStyles = quoteTextStyles(isLongQuote)
    return (
        <Box
            w={{ base: 294, sm: 420 }}
            p={{ base: 24, sm: 48 }}
            pos="relative"
            bg="rgba(45, 26, 255, 0.10)"
            style={{
                flexShrink: 0,
                boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
                overflow: 'hidden',
                borderRadius: CARD_RADIUS,
            }}
        >
            <Flex direction="column" justify="space-between" h="100%" style={{ flex: 1, minHeight: 0 }}>
                <Text
                    component="p"
                    c="gray.9"
                    fz={{ base: quoteStyles.base.fz, sm: quoteStyles.sm.fz }}
                    fw={quoteStyles.base.fw}
                    lh={quoteStyles.base.lh}
                    m={0}
                    style={{
                        letterSpacing: isSm ? quoteStyles.base.letterSpacing : quoteStyles.sm.letterSpacing,
                    }}
                >
                    “{card.quote}”
                </Text>
                <Text c="gray.7" fz={{ base: 16, sm: 20 }} lh="130%" m={0} fw={450}>
                    {card.name}
                </Text>
            </Flex>
        </Box>
    )
}

function PlayIconWhite() {
    return (
        <svg width="71" height="71" viewBox="0 0 71 71" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M35.5 0C55.1061 2.57704e-07 71 15.8939 71 35.5C71 55.1061 55.1061 71 35.5 71C15.8939 71 2.5772e-07 55.1061 0 35.5C0 15.8939 15.8939 0 35.5 0ZM28.2734 21.0059C27.7601 21.0426 27.288 21.3 26.9795 21.7119C26.6254 22.1849 26.625 23.0322 26.625 24.7266V46.2725C26.625 47.9668 26.6254 48.8142 26.9795 49.2871C27.288 49.6992 27.76 49.9574 28.2734 49.9941C28.8627 50.0362 29.5757 49.5783 31.001 48.6621L47.7588 37.8887C48.9955 37.0936 49.6135 36.6959 49.8271 36.1904C50.0138 35.7488 50.0138 35.2503 49.8271 34.8086C49.6134 34.3033 48.9953 33.9052 47.7588 33.1104L31.001 22.3379C29.5757 21.4217 28.8628 20.9638 28.2734 21.0059Z"
                fill="white"
            />
        </svg>
    )
}

export default function WidgetTestimonialUGC({ categoryName, data: dataProp }: CategoryWidgetProps) {
    const containerRef = useRef<HTMLDivElement>(null)
    const { isSm } = useBreakpoint()
    const [cardStartX, setCardStartX] = useState(0)
    const videoModal = useModal()
    const [selectedVideoUrl, setSelectedVideoUrl] = useState<string>('')

    const data = (dataProp ?? getCategoryPageData(categoryName, 'testimonialUgc')) as TestimonialUgcData | null

    useEffect(() => {
        const el = containerRef.current
        const updateStartX = () => {
            if (!el) return
            const rect = el.getBoundingClientRect()
            const padding = isSm ? 16 : 60
            setCardStartX(Math.max(0, rect.left + padding))
        }
        updateStartX()
        window.addEventListener('resize', updateStartX)
        const observer = new ResizeObserver(updateStartX)
        if (el) observer.observe(el)
        return () => {
            window.removeEventListener('resize', updateStartX)
            observer.disconnect()
        }
    }, [isSm])

    if (!data?.cards?.length) return null

    const handleVideoClick = (videoUrl: string) => {
        if (!videoUrl) return
        setSelectedVideoUrl(videoUrl)
        videoModal.open()
    }

    return (
        <>
            <ModalManager type="video" {...videoModal} videoUrl={selectedVideoUrl} />
            {data.header && (
                <Flex
                    component={motion.div}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: '-100px 0px' }}
                    variants={staggerContainer}
                    direction={isSm ? 'column' : 'row'}
                    gap={{ base: 24, sm: 48 }}
                    px={{ base: 24, sm: 48 }}
                    style={{ minWidth: 0 }}
                    mt={{ base: 16, sm: 60 }}
                    maw={1440}
                    mx="auto"
                    w="100%"
                >
                    <MotionBox variants={textFadeInVariantsNoTransition} style={{ flex: 1, minWidth: 0 }}>
                        <Title {...refreshTitleVariantProps.section}>{data.header.title}</Title>
                    </MotionBox>
                    <MotionBox variants={textFadeInVariantsNoTransition} style={{ flex: 1, minWidth: 0 }}>
                        <Text {...refreshTextVariantProps.body} c="gray.6">
                            {data.header.description}
                        </Text>
                    </MotionBox>
                </Flex>
            )}
            <Box
                id="testimonialUgc"
                ref={containerRef}
                maw={1440}
                mx="auto"
                w="100%"
                mt={{ base: 12, sm: 24 }}
                px={{ base: 16, sm: 24 }}
                // py={CARD_PADDING}
                style={{ overflow: 'visible' }}
            >
                <FullBleedWrapper mb={48}>
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, margin: '-80px 0px' }}
                        variants={slideInVariants}
                        style={{ width: '100%' }}
                    >
                        <CardSlider
                            gap={CARD_GAP}
                            startX={cardStartX}
                            endGap={96}
                            useTranslateForStartX
                            initialPosition="end"
                        >
                            {data.cards.map((card, index) => (
                                <CardSliderCard key={`${card.type}-${index}`} radius={CARD_RADIUS}>
                                    {card.type === 'video' ? (
                                        <VideoCard
                                            card={card}
                                            onPlayClick={() => handleVideoClick(card.videoUrl)}
                                            isSm={isSm}
                                        />
                                    ) : card.type === 'image' ? (
                                        <ImageCard card={card} isSm={isSm} />
                                    ) : card.type === 'quote' ? (
                                        <QuoteCard card={card} isSm={isSm} />
                                    ) : null}
                                </CardSliderCard>
                            ))}
                        </CardSlider>
                    </motion.div>
                </FullBleedWrapper>
            </Box>
        </>
    )
}
